import type { ChatMessage } from '../types/chat'
import { deepEqualJson } from './stable-list'

/**
 * Merge polled/subscription messages without reordering flicker or dropping
 * optimistic sends.
 *
 * Identity-stable: a message object from `prev` is reused whenever the merged
 * result is content-equal, and the `prev` ARRAY itself is returned when the
 * merge changes nothing. The open thread re-polls every 5s, so without this
 * every poll tick handed React a brand-new array of brand-new objects and the
 * entire feed re-rendered (visible as scroll/typing jank on the WebViews).
 */
export function mergeChatMessages(prev: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  if (incoming.length === 0) return prev
  const byId = new Map(prev.map((m) => [m.id, m]))
  let changed = false
  for (const m of incoming) {
    const old = byId.get(m.id)
    const merged =
      !m.reactions?.length && old?.reactions?.length
        ? { ...m, reactions: old.reactions }
        : m
    if (old !== undefined && (old === merged || deepEqualJson(old, merged))) continue
    byId.set(m.id, merged)
    changed = true
  }
  if (!changed && byId.size === prev.length) return prev
  return [...byId.values()].sort((a, b) => a.createdAt - b.createdAt)
}

/**
 * Drop optimistic `pending-` rows that the server has since confirmed.
 *
 * When a send mutation's ACK is lost (network drop, webview suspend) but the
 * message actually went through, the real row arrives via the subscription
 * echo or the 5s poll — without this, the confirmed message AND a forever-
 * "Sending…" optimistic bubble both stay in the thread.
 *
 * Matching is heuristic: same sender, same visible text, same has-attachment
 * shape, created within a short window. Each confirmed row consumes at most
 * one pending row so double-sends of identical text aren't over-collapsed.
 */
const PENDING_MATCH_WINDOW_MS = 2 * 60_000

export function reconcilePendingSends(
  messages: ChatMessage[],
  currentUserId: string | undefined,
): ChatMessage[] {
  if (!currentUserId) return messages
  const hasPending = messages.some(
    (m) => m.id.startsWith('pending-') && m.senderId === currentUserId,
  )
  if (!hasPending) return messages

  const confirmed = messages.filter(
    (m) => !m.id.startsWith('pending-') && m.senderId === currentUserId,
  )
  if (confirmed.length === 0) return messages

  const consumed = new Set<string>()
  const resolvedPendingIds = new Set<string>()
  for (const pending of messages) {
    if (!pending.id.startsWith('pending-') || pending.senderId !== currentUserId) continue
    const match = confirmed.find(
      (c) =>
        !consumed.has(c.id) &&
        Math.abs(c.createdAt - pending.createdAt) < PENDING_MATCH_WINDOW_MS &&
        c.text === pending.text &&
        Boolean(c.attachmentUrl) === Boolean(pending.attachmentUrl),
    )
    if (match) {
      consumed.add(match.id)
      resolvedPendingIds.add(pending.id)
    }
  }
  if (resolvedPendingIds.size === 0) return messages
  return messages.filter((m) => !resolvedPendingIds.has(m.id))
}

export function patchMessageReactions(
  messages: ChatMessage[],
  messageId: string,
  reactions: ChatMessage['reactions'],
): ChatMessage[] {
  if (reactions == null) return messages
  return messages.map((m) => (m.id === messageId ? { ...m, reactions } : m))
}
